import ErrorStatus from '$shared/constants/ErrorStatus';
import { ExceptionsToBeLoggedToSentry } from '$shared/constants/ExceptionsToBeLoggedToSentry';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { ApiException } from '$shared/exceptions/ApiException';
import { sentry } from '$shared/helpers/logger/sentry';
import ServiceError from '$shared/models/ServiceError';
import { ErrorHandler, type ErrorStatusMapping } from '$shared/lib/errorHandlers';
import { AlertDetailedInfo } from '$shared/models/AlertDetailedInfo';
import type { ErrorMessage } from '$shared/models/ErrorMessage';
import ApiError from '$shared/models/api/ApiError';

interface IErrorMessage {
  title?: string;
  message?: string;
  code?: number;
}

export const getErrorMessage = (
  status: ErrorStatus,
  error: ApiError | Error,
  statusMapping?: ErrorStatusMapping
): IErrorMessage => {
  const errorMessage: IErrorMessage | undefined = statusMapping?.[status];
  let title = errorMessage?.title || error.message;
  let message = errorMessage?.message || error.message;

  switch (status) {
    case ErrorStatus.ServerError:
    case ErrorStatus.GatewayTimeoutError: {
      title = UIErrorMessage.ServerError;
      break;
    }
    case ErrorStatus.UnHandledException: {
      title = title || UIErrorMessage.UnknowError;
      break;
    }
    case ErrorStatus.Forbidden: {
      const predefinedError: IErrorMessage | undefined = statusMapping?.[ErrorStatus.Forbidden];

      title = predefinedError?.title || title || UIErrorMessage.ActionForbidden;
      message = predefinedError?.message || message || UIErrorMessage.ActionForbidden;
      break;
    }
    case ErrorStatus.BadRequest:
    case ErrorStatus.Conflict:
    case ErrorStatus.NotFound:
    case ErrorStatus.Maintenance:
    case ErrorStatus.HandledUnknownException: {
      title = title || '';
      break;
    }
  }

  // Deriving HTTP code
  const code =
    (error as any)?.response?.status ??
    (error as any)?.status ??
    status;

  return { title, message, code };
};

const thorwError = (
  error: ApiError | Error,
  methodName: string,
  args: any,
  statusMapping?: ErrorStatusMapping
): void => {
  if (error instanceof ServiceError) {
    throw error;
  }
  const status = error instanceof ApiError ? error.status : ErrorStatus.UnHandledException;
  const errorMessage: ErrorMessage = ErrorHandler.getMessage(status, error, statusMapping);
  const serviceError = ServiceError.create(status, errorMessage, (error as ApiError)['errors']);

  // Set alert info to service error and it will use it for the detail alert UI.
  serviceError.setDetailErrorAlert(AlertDetailedInfo.fromApiError(serviceError));

  if (ExceptionsToBeLoggedToSentry.indexOf(status) > -1 || !!errorMessage?.sendToSentry) {
    const exception = ApiException.from(
      'ApiErrorWrapper',
      methodName,
      args,
      serviceError,
      {
        status,
        apiError: error,
        ...errorMessage?.toSentryData()
      },
      !!errorMessage?.sendToSentry
    );
    exception.setIgnoreErrorWhitelist(errorMessage?.ignoreErrorWhitelist);
    exception.setWarnErrorWhiteList(errorMessage?.warnErrorWhiteList);
    sentry.log(exception);
  }

  throw serviceError;
};

const ApiErrorWrapper = (statusMapping?: ErrorStatusMapping) => {
  return (
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    _target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ): PropertyDescriptor => {
    const originalMethod = descriptor.value;
    descriptor.value = function (...args: any[]) {
      try {
        const result = originalMethod.apply(this, args);
        if (result && result instanceof Promise) {
          return result.catch((error: ApiError | Error) => {
            thorwError(error, propertyKey, args, statusMapping);
          });
        }
        return result;
      } catch (error: any) {
        thorwError(error, propertyKey, args, statusMapping);
      }
    };
    return descriptor;
  };
};

export default ApiErrorWrapper;
